import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { KnownIssue } from '../api/useKnownIssues.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { saksnr } from '../innboks/saksnummer.ts';
import { Ikon } from '../status/Ikon.tsx';
import { KjentFeilPille } from '../status/Pille.tsx';
import { skjulesForNyereEnn } from '../status/status.ts';
import { Om } from '../skall/Om.tsx';
import { Bryter } from './Bryter.tsx';
import { skjermTekst, versjonTekst } from './Rad.tsx';

/** Låsegrunnen for rollen leser på hele flaten — ett sted (KjenteFeilSide, Skjema, Detalj). */
export const LESER_GRUNN_KF = 'Rollen leser kan ikke endre kjente feil';

export interface KjentFeilDetaljProps {
  f: KnownIssue;
  /** Produktnavnet lest opp fra `productId`; kontrakten har ingen plattform på en kjent feil. */
  produktNavn?: string;
  kanSkrive: boolean;
  onBryter: (felt: 'visibleInWidget' | 'visibleToTesters', verdi: boolean) => void;
  onRettet: () => void;
  onRediger: () => void;
}

/**
 * Detaljruten (R3.3 — kjente feil-konsept v0.1 skjerm 1, 2 og 5):
 *
 *   Kjente feil · produkt · fra sak
 *   HELTEN: tittelen med pillen under, og «Har spurt N» som skjermens ene display-tall
 *   DD-30s deler som rader: Omvei · Rettes i / Rettet i · Gjelder · Vises
 *   tre tall (DD-29) der de to første ER lenkene, med «Om tallene» bak en linje
 *   «Slik ser innsenderen kortet» (DD-03) bak en linje
 *   festet fot (DD-58, P2): «Merk som rettet …» i action-fargen på aktive, «Rediger»
 *
 * Reglene står ikke på skjermen (F3, F15): de bor bak «Om tallene» og «Om skjermer,
 * versjoner og hvem som ser kortet» i skjemaet. At Rettet er endelig (DD-31) sies av at
 * knappen ikke finnes på en rettet feil — aldri av en setning i foten.
 */
export function KjentFeilDetalj({ f, produktNavn, kanSkrive, onBryter, onRettet, onRediger }: KjentFeilDetaljProps) {
  const rettet = f.status === 'fikset';
  const grunn = kanSkrive ? null : LESER_GRUNN_KF;

  const rader: Array<[string, ReactNode]> = [];
  if (rettet) {
    rader.push([
      'Rettet i',
      <>
        {f.fixedIn}
        {f.fixedIn ? <span className="panel-kf-biledd"> · {skjulesForNyereEnn(f.fixedIn)}</span> : null}
      </>,
    ]);
  }
  rader.push(['Omvei', f.workaround ? f.workaround : <span className="panel-kf-tomt">Ingen omvei oppgitt</span>]);
  if (!rettet) rader.push(['Rettes i', f.fixedIn ? f.fixedIn : <span className="panel-kf-tomt">ikke satt</span>]);
  rader.push([
    'Gjelder',
    <>
      {skjermTekst(f)}
      <span className="panel-kf-biledd"> · {versjonTekst(f)}</span>
    </>,
  ]);
  rader.push([
    'Vises',
    <span className="panel-kf-synlighet">
      <span className="panel-kf-brytere">
        <Bryter label="I widgeten" paa={f.visibleInWidget} grunn={grunn} onChange={(v) => onBryter('visibleInWidget', v)} testid={`kf-widget-${f.id}`} />
        <Bryter label="I programmet" paa={f.visibleToTesters} grunn={grunn} onChange={(v) => onBryter('visibleToTesters', v)} testid={`kf-beta-${f.id}`} />
      </span>
      {/* Tilstandstekst, ikke hjelpetekst: den sier hva valget betyr nå (P3). */}
      {!f.visibleInWidget && f.visibleToTesters ? <span className="panel-kf-tilstand">Vises bare i programmet</span> : null}
      {!f.visibleInWidget && !f.visibleToTesters ? (
        <span className="panel-kf-tilstand panel-kf-tilstand-varsel">
          <Ikon navn="warning-outline" /> Ingen ser denne — den finnes bare i registeret.
        </span>
      ) : null}
    </span>,
  ]);

  return (
    <section className="panel-detalj panel-kf-detalj" aria-labelledby="kf-detalj-h" data-testid="kf-detalj">
      <p className="panel-detalj-meta">
        Kjente feil
        {produktNavn ? ` · ${produktNavn}` : ''}
        {f.sourceSubmissionNumber ? ` · fra sak ${saksnr(f.sourceSubmissionNumber)}` : ''}
      </p>

      <div className="panel-detalj-helt">
        <div className="panel-kf-hode">
          <h2 id="kf-detalj-h" className="panel-detalj-tittel">
            {f.title}
          </h2>
          <p className="panel-kf-pille">
            <KjentFeilPille status={f.status} />
          </p>
        </div>
        <p className="panel-detalj-reach" data-testid="kf-helt">
          <span className="panel-etikett">Har spurt</span>
          <span className="panel-detalj-reach-tall">{f.reach}</span>
        </p>
      </div>

      <dl className="panel-kf-rader" data-testid="kf-rader">
        {rader.map(([navn, verdi]) => (
          <div key={navn} className="panel-kf-rad-par">
            <dt>{navn}</dt>
            <dd>{verdi}</dd>
          </div>
        ))}
      </dl>

      {/* DD-29: de to første tallene ER lenkene, så tallet ikke står to steder. */}
      <div className="panel-kf-tall">
        <span className="panel-kf-tall-ett" data-testid="kf-tall-ja">
          <Link to={`/saker?kjentFeil=${f.id}`} className="panel-knapp-lenke panel-kf-tall-lenke">
            Ja, jeg også · Saker
            <Ikon navn="chevron-forward-outline" />
          </Link>
          <span className="panel-kf-tall-verdi">{f.confirmations}</span>
        </span>
        <span className="panel-kf-tall-ett" data-testid="kf-tall-ligner">
          <Link to="/innboks" className="panel-knapp-lenke panel-kf-tall-lenke">
            Ligner · Innboks
            <Ikon navn="chevron-forward-outline" />
          </Link>
          <span className="panel-kf-tall-verdi panel-kf-tall-dempet">{f.related}</span>
        </span>
        <span className="panel-kf-tall-ett" data-testid="kf-tall-anonyme">
          <span className="panel-etikett">Anonyme</span>
          <span className="panel-kf-tall-verdi panel-kf-tall-dempet">{f.anonymousCount}</span>
        </span>
      </div>
      <Om tittel="tallene" testid="kf-om-tallene">
        <p>«Har spurt» er antall distinkte innsendere bak «ja, jeg også» og «ligner, men ikke helt» — et rått tall, aldri en score og aldri en rangering.</p>
        <p>«Ligner, men ikke helt» er lenket, ikke bekreftet: den avgjøres på saken i Innboksen. Anonyme innsendere telles ikke, og de kan ikke varsles når feilen rettes.</p>
      </Om>

      {/* DD-03: kortet slik widgeten viser det — ren visning, ingen handling. */}
      <details className="panel-om" data-testid="kf-kort">
        <summary className="panel-om-sammendrag">
          <Ikon navn="eye-outline" /> <span>Slik ser innsenderen kortet</span>
        </summary>
        <div className="panel-om-innhold">
          <div className="panel-kf-kort">
            <strong className="panel-kf-kort-tittel">{f.title}</strong>
            {f.workaround ? (
              <span className="panel-kf-kort-omvei">
                <Ikon navn="return-down-forward-outline" /> Omvei: {f.workaround}
              </span>
            ) : null}
            <span className="panel-kf-kort-status">
              <KjentFeilPille status={f.status} />
              {f.fixedIn ? (
                <span className="panel-kf-biledd">
                  {rettet ? 'Rettet' : 'Rettes'} i {f.fixedIn}
                </span>
              ) : null}
            </span>
            <span className="panel-kf-kort-svar">
              <span>Ja, jeg også</span>
              <span>Ligner, men ikke helt</span>
            </span>
          </div>
        </div>
      </details>

      {/* Foten (DD-58, P2): ÉN knapp i action-fargen. «Merk som rettet …» er dialogens tittel,
          så det som ÅPNER heter noe annet enn det som varsler (DD-60). Tallet står i dialogen. */}
      <footer className="panel-detalj-fot">
        {f.status === 'aktiv' ? <Knapp label="Merk som rettet …" kbd="x" primaer grunn={grunn} onClick={onRettet} testid={`kf-rettet-${f.id}`} /> : null}
        <Knapp label="Rediger" kbd="Enter" grunn={grunn} onClick={onRediger} testid="kf-rediger" />
      </footer>
    </section>
  );
}
